import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** What happened to the resource. */
export type AuditChangeTypeV3 = 'CREATED' | 'UPDATED' | 'DELETED';

/** One entity change as the v3 audit API lists it. The versions are the full entity, with secrets redacted. */
export interface AuditEventV3 {
    [key: string]: any;

    id: string;
    scopeId?: string;
    /** The authorization code of what changed: `share`, `agent`, `frontdoor`. */
    resourceType?: string;
    /** Tells two classes with one type code apart: `tcp`, `ipsec` or `http` for a share. */
    resourceKind?: string;
    resourceId?: string;
    /** The resource's ancestry, as type code to id: `{frontdoor: <id>, share: <id>}`. */
    resourcePath?: Record<string, string>;
    changeType?: AuditChangeTypeV3;
    /** Ties together the changes that one request caused. */
    correlationId?: string;
    traceId?: string;
    occurredAt?: string;
    recordedAt?: string;
    /** `name` is the actor's name now; `nameAtEvent` is the name stored with the event. */
    actor?: { id?: string; name?: string; nameAtEvent?: string };
    /** JSON pointers of the leaves that differ between the two versions, `/name`. */
    changedPaths?: string[];
    /** Absent on a create. */
    fromVersion?: Record<string, any>;
    /** Absent on a hard delete. */
    toVersion?: Record<string, any>;
    _links?: Record<string, { href: string }>;
}

/** One page of audit events, plus the cursor that gets the next one. */
export interface AuditEventPageV3 {
    events: AuditEventV3[];
    /** The opaque cursor of the next page, or '' on the last page. */
    nextCursor: string;
}

/**
 * What to narrow the listing by. A list matches any of its values, and the service ANDs the fields.
 * The service accepts at most 25 values in one list.
 */
export interface AuditEventFilterV3 {
    /** Words that must all appear in the resource's values. Case does not matter. */
    q?: string;
    changeTypes?: AuditChangeTypeV3[];
    resourceTypes?: string[];
    resourceKinds?: string[];
    actorIds?: string[];
    traceIds?: string[];
    /** The window the change occurred in, as ISO instants: `from` inclusive, `to` exclusive. */
    from?: string;
    to?: string;
}

/** Each list field and the repeated query parameter it goes as. */
const AUDIT_LIST_PARAMS: [keyof AuditEventFilterV3, string][] = [
    ['changeTypes', 'changeType'],
    ['resourceTypes', 'resourceType'],
    ['resourceKinds', 'resourceKind'],
    ['actorIds', 'actorId'],
    ['traceIds', 'traceId'],
];

export const DEFAULT_AUDIT_PAGE_SIZE = 25;

/**
 * Reads the entity-change audit from the v3 frontdoor API, one frontdoor at a time. The listing is
 * newest first and pages by an opaque cursor, so it has no total and no page numbers.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3AuditService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /** One page of `frontdoorId`'s audit events. Pass no cursor for the first page. */
    public findAuditEvents(
        frontdoorId: string,
        filter: AuditEventFilterV3 = {},
        cursor = '',
        size = DEFAULT_AUDIT_PAGE_SIZE
    ): Observable<AuditEventPageV3> {
        let params = new HttpParams().set('size', String(size));
        if (cursor) {
            params = params.set('cursor', cursor);
        }
        (['q', 'from', 'to'] as const).forEach((key) => {
            if (filter[key]) {
                params = params.set(key, filter[key] as string);
            }
        });
        // Appended, not set, so several values stay several parameters.
        AUDIT_LIST_PARAMS.forEach(([key, param]) => {
            ((filter[key] as string[]) ?? []).forEach((value) => {
                params = params.append(param, value);
            });
        });
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/audit-events`, { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    /* Internals -------------------------------------------------------------- */

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    private readPage(response: any): AuditEventPageV3 {
        return { events: this.readList(response), nextCursor: cursorOf(response?._links?.next?.href) };
    }

    private readList(response: any): AuditEventV3[] {
        const embedded = response?._embedded;
        if (embedded) {
            const collection = embedded.auditEvents;
            if (Array.isArray(collection)) {
                return collection;
            }
            this.logger.error('findAuditEvents: unrecognised response shape', response);
            return [];
        }
        // An empty HAL collection carries no `_embedded` at all.
        return [];
    }
}

/** The `cursor` query parameter of a `next` link, or ''. Read out so a page can be re-requested with other filters kept. */
function cursorOf(href?: string): string {
    if (!href) {
        return '';
    }
    const query = href.split('?')[1] ?? '';
    return new HttpParams({ fromString: query }).get('cursor') ?? '';
}
